<template>
  <div>
    <BaseFormCard
      :heading="submitName"
      :submit-title="submitName"
      :submit-promise="submit"
    >
      <template #info>
        <div
          v-for="prop in infoProps"
          :key="prop.title"
        >
          <div class="font-weight-bold p-1">
            {{ prop.title }}
          </div>
          <table>
            <tr
              v-for="option in prop.oneOf"
              :key="option.title"
              class="mb-1"
            >
              <td
                class="font-weight-bold text-nowrap p-1"
                style="vertical-align: top;"
              >
                {{ option.title }}
              </td>
              <td class="p-1">
                {{ option.description }}
              </td>
            </tr>
          </table>
        </div>
      </template>
      <BaseInput
        v-if="projectId"
        :value="project.id"
        title="ID"
        name="project_id"
        readonly
      />
      <BaseInput
        v-model="project.name"
        :maxlength="64"
        title="Ticket Title"
        name="name"
        type="text"
        required
        trim
      />
      <BaseSelect
        v-model="project.project_type"
        :options="projectTypes"
        :readonly="Boolean(project.project_type)"
        title="Delivery Group"
        name="project_type"
        track-by="name"
        label="name"
        placeholder=""
        required
        @input="getGroupData"
      />
      <span v-if="project.project_type">
        <template v-if="!projectId">
          <FieldRoleUser
            v-for="role in roles"
            :key="role.usr_role.id"
            v-model="role.usr"
            :role="role.usr_role"
            :required="role.usr_role.required_state.is_start"
          />
        </template>
        <SchemaPropertyField
          v-for="prop in editProps"
          :key="prop.fullname"
          v-model="prop.base[prop.name]"
          :name="prop.fullname"
          :schema="prop.schema"
          :required="prop.required"
        />
        <template v-if="!projectId && attachmentTypes && attachmentTypes.length > 0">
          <BaseInputFile
            v-for="at in attachmentTypes"
            :key="at.name"
            v-model="attachments[at.id + '_' + at.name]"
            :max-size="maxFileSize"
            :title="at.name"
            :name="at.name"
            type="file"
            required
          />
        </template>
      </span>
      <FieldError v-model="error" />
    </BaseFormCard>
  </div>
</template>

<script>
/**
 * View to Create or Update a project.
 * Emits a 'reload-project' when a project update is succesful.
 */
import FieldError from '@/components/FieldError'
import FieldRoleUser from '@/components/FieldRoleUser'
import groupAPI from '@/api/group'
import projectAPI from '@/api/project'
import SchemaPropertyField from '@/components/SchemaPropertyField'
import { mapState } from 'vuex'

export default {
  components: {
    FieldError,
    FieldRoleUser,
    SchemaPropertyField,
  },
  props: {
    /**
     * Optional project id.
     * If supplied will edit this project.
     * If a new project will be created.
     */
    projectId: {
      type: [String, Number],
      default: '',
    },
  },
  data () {
    return {
      project: {
        name: '',
        properties: {},
        project_type: undefined,
        p4p: {
          clarity_code: '',
          pan_code: '',
          release_id: '',
          work_required: '',
          delivery_date: '',
        },
      },
      error: '',
      projectTypes: [],
      attachmentTypes: [],
      attachments: {},
      roles: [],
      minDate: '',
      disabledDays: null,
      disabledDates: null,
      schema: { properties: {} },
    }
  },
  computed: {
    submitName () {
      if (this.projectId) {
        return 'Update'
      }
      return 'Create'
    },
    canEditDeliveryDate () {
      // Can edit if creating a new project or editing a newly created (or rejected) project.
      return !this.projectId ||
        (this.project && this.project.project_state &&
          (this.project.project_state.is_start || this.project.project_state.reset_project))
    },
    editProps () {
      return this.getObjectProps(this.schema, this.project)
    },
    maxFileSize () {
      return this.currentUser.max_attachment_size_mb * 1024
    },
    infoProps () {
      // find props whose options have 'description' fields
      return this.editProps
        .map(p => p.schema)
        .filter(p => p.oneOf && p.oneOf.filter(prop => !!prop.description).length > 0)
    },
    ...mapState('user', ['currentUser']),
  },
  beforeMount () {
    this.getData()
  },
  methods: {
    getObjectProps (schema, data) {
      let props = schema['ui:order'] || []
      if (props.length === 0) {
        props = Object.keys(schema.properties || {}).sort()
      }
      props = props.filter(p => schema.properties[p] && !schema.properties[p].readOnly)
      return props.map(p => {
        const propSchema = schema.properties[p]
        if (propSchema.type === 'object') {
          // get nested props
          return this.getObjectProps(propSchema, data[p]).map(op => {
            return { ...op, fullname: p + '.' + op.name }
          })
        }
        return {
          name: p,
          schema: propSchema,
          base: data,
          fullname: name,
          required: schema.required && schema.required.includes(p),
        }
      }).flat()
    },
    async submit () {
      if (this.projectId) {
        await projectAPI.updateProject(this.project)
        this.$router.go(-1)
      } else {
        await this.createProject()
        if (this.project.project_type.use_subcats) {
          this.$router.replace({ name: 'update_project_services', params: { projectId: this.project.id, showCatalog: true } })
        } else {
          this.$router.replace({ name: 'show_project_info', params: { projectId: this.project.id } })
        }
      }
    },
    getGroupData (projectType) {
      this.roles = []
      this.attachmentTypes = []
      Promise.all([
        groupAPI.getNewProjectSchema(projectType.id),
        groupAPI.getGroupRoles(projectType.id),
        groupAPI.getAttachmentTypes(projectType.id),
        groupAPI.getProjectStates(projectType.id),
      ]).then(([schema, roles, attachmentTypes, states]) => {
        if (!this.projectId) {
          this.schema = schema.data
        }
        this.roles = roles.data.filter(r => r.create_at_start).map(r => { return { usr_role: r, usr: {} } })
        const startState = states.data.find(s => s.is_start)
        if (startState && startState.id) {
          this.attachmentTypes = attachmentTypes.data.filter(at => at.required_state_id === startState.id)
        }
      }).catch(error => {
        this.error = error.message
      })
    },
    createFormData () {
      const formData = new FormData()
      let fileCount = 0

      // set file name to work around IE including full path in attachment upload
      for (const atType in this.attachments) {
        const file = this.attachments[atType]
        formData.append(atType, file, file.name)
        fileCount++
      }
      if (fileCount === 0) {
        return undefined
      }
      return formData
    },
    createProject () {
      let projId = ''
      this.project.roles = this.roles
      return projectAPI.createProject(this.project).then(resp => {
        this.$parent.$toasted.success('Ticket ' + this.project.name + ' created')
        projId = resp.data.id
        this.project.id = resp.data.id
        const formData = this.createFormData()
        if (formData) {
          return projectAPI.uploadAttachment(projId, formData)
        }
        return Promise.resolve()
      }).catch(error => {
        if (projId) {
          this.$parent.$toasted.error('Attachment upload failed: ' + error.message)
          this.$router.push({ name: 'show_project_info', params: { projectId: projId } })
        }
        return Promise.reject(error)
      })
    },
    getData () {
      const requests = [
        groupAPI.getGroups(),
      ]
      if (this.projectId) {
        requests.push(projectAPI.getProject(this.projectId))
        requests.push(projectAPI.getSchema(this.projectId))
      }
      Promise.all(requests).then(([projectTypes, project, schema]) => {
        this.projectTypes = projectTypes.data
        if (project) {
          this.project = project.data
          this.schema = schema.data
          this.getGroupData(this.project.project_type)
        } else if (this.projectTypes.length === 1) {
          // If there is only one group then set it to it
          this.project.project_type = this.projectTypes[0]
          this.getGroupData(this.project.project_type)
        }
      }).catch(error => {
        this.error = error.message
      })
    },
    goBack () {
      this.$router.go(-1)
    },
  },
}
</script>
